import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  ChatCircleDots,
  Compass,
  FileText,
  Target,
} from "@phosphor-icons/react";
import SeoPageShell from "@/components/SeoPageShell";
import WhistleIcon from "@/components/WhistleIcon";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "sales_call_debrief_template";

const debriefSteps = [
  {
    icon: ChatCircleDots,
    label: "Evidence",
    title: "What was actually said?",
    copy: "Pull out the moments that changed the direction of the conversation. Do not start with your interpretation—start with the evidence.",
  },
  {
    icon: Brain,
    label: "What changed",
    title: "What is different now?",
    copy: "Identify the shift in urgency, risk, decision process, stakeholder dynamics, objections, or buying intent that matters after the call.",
  },
  {
    icon: WhistleIcon,
    label: "Better approach",
    title: "What could you have handled differently?",
    copy: "Choose one or two moments where another question, framing, or response could have produced a better conversation—not a list of generic mistakes.",
  },
  {
    icon: Target,
    label: "Next move",
    title: "What should happen next?",
    copy: "Turn the debrief into action: what to validate, what to ask, what to avoid, and what outcome the next conversation needs to create.",
  },
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function SalesCallDebriefTemplatePage() {
  return (
    <SeoPageShell source={source}>
      <main>
        <section className="seo-marketing-hero product-feature-hero relative overflow-hidden px-5 pb-20 pt-16 sm:px-8 sm:pb-24 sm:pt-24">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className="absolute left-[-12%] top-[-14rem] h-[36rem] w-[36rem] rounded-full bg-indigo-700/20 blur-[135px]" />
            <div className="absolute right-[-8%] top-[2rem] h-[28rem] w-[28rem] rounded-full bg-cyan-700/10 blur-[135px]" />
          </div>
          <div className="relative mx-auto max-w-7xl">
            <h1 className="seo-marketing-hero__title product-feature-hero__title landing-hero-title max-w-5xl">
              Sales Call Debrief Template
              <span className="sales-call-review-gradient product-feature-hero__title-accent landing-hero-spectrum block">that turns a call into the next move.</span>
            </h1>
            <p className="seo-marketing-hero__subcopy product-feature-hero-subcopy landing-hero-subcopy marketing-lead-copy">
              A good debrief should help you reconstruct what happened, understand what changed, identify a better approach, and carry the lesson into the next conversation. Use this framework instead of writing a longer recap.
            </p>
            <div className="seo-marketing-hero__actions product-feature-hero__actions">
              <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">Sign up free</Link>
              <Link to="/demo" onClick={() => track("hero", "see_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">See Demo<ArrowRight size={17} weight="bold" /></Link>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <span className="sales-call-review-kicker">The four-part debrief</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Debrief the call in the order that makes the reasoning useful.</h2>
              <p className="marketing-lead-copy mt-6">Evidence first. Interpretation second. Coaching third. Action last. Skipping that order is how a debrief turns into opinion or generic advice.</p>
            </div>
            <div className="mt-12 grid gap-5 md:grid-cols-2">
              {debriefSteps.map(({ icon: Icon, label, title, copy }, index) => (
                <article key={label} className="sales-call-review-card">
                  <div className="sales-call-review-card__topline"><span className="sales-call-review-card__number">0{index + 1}</span><Icon size={21} weight="duotone" /></div>
                  <span className="sales-call-review-speaker">{label}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">Copyable template</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Use the same questions after every important call.</h2>
              <p className="marketing-lead-copy mt-6">The structure stays stable. The answer changes with the customer.</p>
            </div>
            <div className="sales-call-review-memory">
              <div className="sales-call-review-memory__row"><span>EVIDENCE</span><strong>Which 2–4 moments changed the conversation?</strong><small>Quote or paraphrase only what materially affected the deal.</small></div>
              <div className="sales-call-review-memory__row"><span>CHANGE</span><strong>What is true now that was not true before the call?</strong><small>Risk, urgency, stakeholder, objection, commitment, or decision process.</small></div>
              <div className="sales-call-review-memory__row"><span>BETTER APPROACH</span><strong>What would you do differently in one important moment?</strong><small>Write the alternative question, framing, or wording you would actually use.</small></div>
              <div className="sales-call-review-memory__row is-next"><span>NEXT MOVE</span><strong>What should the next conversation accomplish?</strong><small>Define what to validate, ask, avoid, and move forward.</small></div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.06] bg-white/[0.018] px-5 py-16 sm:px-8 sm:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-start lg:gap-16">
            <div className="lg:sticky lg:top-24">
              <span className="sales-call-review-kicker">Worked example</span>
              <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">A debrief should change what you do next.</h2>
              <p className="marketing-lead-copy mt-6">If the output does not influence the next conversation, it is probably still just documentation.</p>
            </div>
            <div className="sales-call-review-example">
              <div className="sales-call-review-example__quote"><span className="sales-call-review-speaker">CLIENT</span><FileText size={22} weight="duotone" /><blockquote>“I can get my manager on board. Procurement is the part I’m less sure about, and I do not know what they will ask us for.”</blockquote></div>
              <div className="sales-call-review-example__analysis">
                <div><span>WHAT CHANGED</span><p>Interest is not the blocker. The unresolved risk is the internal approval path and the champion's lack of confidence around procurement.</p></div>
                <div><span>BETTER APPROACH</span><p>Do not leave the internal sale to the champion. Map procurement while you still have the customer in the conversation.</p></div>
                <div><span>EXAMPLE WORDING</span><p>“Before we leave this with you, who normally gets involved from procurement and what evidence do they usually want before they approve something like this?”</p></div>
              </div>
              <div className="sales-call-review-example__next"><span>NEXT MOVE</span><p>Use the next call to validate the approval path, identify procurement requirements, and agree who will carry the internal case.</p></div>
            </div>
          </div>
        </section>

        <section className="px-5 py-16 sm:px-8 sm:py-20">
          <div className="conversation-intelligence-related mx-auto max-w-5xl">
            <div><span className="sales-call-review-kicker">Review the call first</span><h2>A debrief works best when it stays anchored to evidence.</h2><p>See the broader review framework for separating what happened, what it meant, what to coach, and what should happen next.</p></div>
            <Link to="/sales-call-review" onClick={() => track("internal_link", "sales_call_review")} className="marketing-consistency-secondary-link product-feature-final__secondary">Sales call review<ArrowRight size={16} weight="bold" /></Link>
          </div>
        </section>

        <section className="px-5 pb-20 pt-4 sm:px-8 sm:pb-28">
          <div className="product-feature-final sales-call-review-cta mx-auto max-w-5xl">
            <span className="sales-call-review-kicker">Automate the debrief</span>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">Let the evidence, coaching, and next move stay connected.</h2>
            <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">Callsect turns the conversation into evidence-backed analysis, practical Coaching, relationship context, and next-call preparation without reducing the debrief to a score.</p>
            <div><Link to="/product/coaching" onClick={() => track("final", "see_coaching")} className="product-feature-final__primary">See Coaching</Link><Link to="/demo" onClick={() => track("final", "see_demo")} className="product-feature-final__secondary">See Demo<ArrowRight size={17} weight="bold" /></Link></div>
          </div>
        </section>
      </main>
    </SeoPageShell>
  );
}
